import React from 'react';
import {
  ExchangeRetryStatus,
  openPasswordLogin,
  startNativeLogin,
  subscribeExchangeRetry,
  subscribeUnlockPrompt
} from 'services/biometricLoginEntry';
import { getBiometryLabel } from 'services/nativeBiometric';

import { BiometryKind } from 'components/BiometricPrototype/biometricGlyphs';
import {
  BiometricsChangedCard,
  BiometricSurfaceHost,
  ConnectionTroubleCard,
  LockedOutCard,
  UnlockRetryCard
} from 'components/BiometricPrototype/BiometricSurfaces';

/**
 * The AC13 surface: what the player sees when a biometric unlock does not
 * complete (QA blocker — Noe 2026-08-17, re-tested 8/18).
 *
 * Before this, ANY failed or cancelled scan opened the auth app in the system
 * browser — the "kicked to Login/SignUp" QA filed — and a hard failure
 * disabled the setting on the way out. Now `startNativeLogin` publishes the
 * outcome here (see `subscribeUnlockPrompt` for why a registry and not props:
 * five different tap targets reach that service through a fire-and-forget
 * util), and this mounts the matching card over wherever the player already
 * was:
 *
 *  - not-completed → UnlockRetryCard. Retry re-runs the scan from the card —
 *    the same in-place pattern as the enrollment soft landing. "Use another
 *    login method" is held back until MAX_SCAN_ATTEMPTS is actually used up
 *    (see the doc comment below); once it is, Retry is REPLACED by the
 *    fallback rather than joined by it — only one of the two ever renders.
 *    Enrollment intact either way.
 *  - locked-out → LockedOutCard (AC12). Same replace-not-join fallback rule
 *    as above. "Try again" re-runs the scan — the OS lockout is often
 *    per-attempt or clears on its own, so a retry can genuinely succeed;
 *    re-raising this same card otherwise is a truthful outcome, not a broken
 *    one. Enrollment intact either way.
 *  - turned-off → BiometricsChangedCard (AC9's player-facing moment). The
 *    stored sign-in stopped working and has been disabled — retrying
 *    biometrics has nothing left to retry, so this is the one card without a
 *    retry button; it says what happened instead of letting the setting flip
 *    off in silence.
 *  - connection-failed → ConnectionTroubleCard. Every retry failed to reach
 *    the server at all, so the credential was never actually checked —
 *    enrollment stays on. "Try again" re-runs biometrics, "Use another login
 *    method" routes to the login page.
 *
 * Dismissing any card keeps the player exactly where they were, logged out,
 * with nothing changed — that is the point of AC13.
 *
 * Also renders the "Reconnecting…" toast (subscribeExchangeRetry) whenever
 * exchangeRefreshToken is retrying a dropped connection — QA asked for a
 * retry that's visible, not one that only shows up in the console. It's a
 * small fixed toast rather than a card because it can fire before any card
 * would otherwise appear (the very first login attempt, with no prior
 * failure), and it always clears itself once the exchange settles either way.
 *
 * The not-completed and locked-out cards get the same "make the retry
 * visible" treatment via an "Attempt X of MAX_SCAN_ATTEMPTS" caption — OUR OWN
 * in-app count of consecutive sensor failures (either card counts — a no-
 * match on one retry and a lockout on the next are both "the sensor didn't
 * work this time" from the player's seat). It resets to 1 whenever a fresh
 * failure arrives after something else, and counts up only while the SAME
 * retry loop keeps going via "Try again".
 *
 * QA (Harvey, 2026-08-26): "Use another login method" is HELD BACK until that
 * count actually runs out — retry is the only option shown while attempts
 * remain, so the player has to exhaust them for real before a different
 * method appears next to it. Both cards enforce this themselves (see
 * `attemptsExhausted` in BiometricSurfaces.tsx); this component only owns the
 * counter.
 */
const MAX_SCAN_ATTEMPTS = 3;

function isScanFailure(
  s: 'not-completed' | 'locked-out' | 'turned-off' | 'connection-failed' | null
): boolean {
  return s === 'not-completed' || s === 'locked-out';
}

export default function BiometricUnlockPrompt() {
  const [state, setState] = React.useState<
    'not-completed' | 'locked-out' | 'turned-off' | 'connection-failed' | null
  >(null);
  const [busy, setBusy] = React.useState(false);
  const [biometry, setBiometry] = React.useState<BiometryKind>('faceid');
  const [retryStatus, setRetryStatus] = React.useState<ExchangeRetryStatus>(null);
  const [scanAttempt, setScanAttempt] = React.useState(1);
  const prevStateRef = React.useRef<typeof state>(null);

  React.useEffect(
    () =>
      subscribeUnlockPrompt((next) => {
        setBusy(false);
        // Read before mutating: the ref must reflect the PREVIOUS outcome
        // when the updater below runs, and setState's functional updaters
        // aren't guaranteed to execute before the ref write on the next
        // line (React's eager-state evaluation only fires synchronously
        // when the hook's queue is empty, which isn't consistent here) —
        // so capture the plain boolean now rather than reading the ref
        // from inside the updater.
        const wasFailure = isScanFailure(prevStateRef.current);
        prevStateRef.current = next;
        setScanAttempt((prev) => (isScanFailure(next) && wasFailure ? prev + 1 : 1));
        setState(next);
      }),
    []
  );

  React.useEffect(() => subscribeExchangeRetry(setRetryStatus), []);

  React.useEffect(() => {
    if (!state) return;
    let cancelled = false;
    void getBiometryLabel().then((label) => {
      if (cancelled) return;
      const l = (label || '').toLowerCase();
      setBiometry(l.includes('face') ? 'faceid' : l.includes('touch') ? 'touchid' : 'fingerprint');
    });
    return () => {
      cancelled = true;
    };
  }, [state]);

  const close = () => {
    // Leaving the card (dismiss, or navigating away on success) ends this
    // failure loop's identity — otherwise the next, independent biometric
    // login failure would still see this session's last outcome in the ref
    // and keep counting up instead of restarting at attempt 1.
    prevStateRef.current = null;
    setScanAttempt(1);
    setState(null);
  };
  const usePassword = () => {
    openPasswordLogin();
    close();
  };
  const retry = () => {
    // In place, like the enrollment retry: the card stays, goes busy, and the
    // next thing the player sees is the OS prompt. The subscription above
    // delivers the next outcome (or 'entered' navigates away entirely).
    //
    // hijack-review: startNativeLogin's own network step (exchangeRefreshToken)
    // is bounded (AbortController, 8s × 3 attempts — see nativeLogin.ts), but
    // the step before it, unlockBiometric()'s native OS biometric prompt
    // (services/nativeBiometric.ts), has no client-side timeout at all — this
    // component awaits whatever the native plugin promise does. If that
    // promise never settles (a stalled/hung native call, as opposed to the OS
    // rejecting on cancel/no-match/lockout, which are all handled above), busy
    // stays true indefinitely and the card is non-dismissible
    // (dismissible={!busy}) with no way out. Not reproduced on device as of
    // this note — flagging as an unverified gap, not a confirmed bug.
    setBusy(true);
    void startNativeLogin()
      .then((outcome) => {
        if (outcome === 'entered' || outcome === 'password-handoff') close();
        // The raise() paths land through the subscription, which clears busy.
      })
      .catch((err) => {
        // Nothing upstream is expected to reject (every known failure path
        // resolves to an outcome string) — but if something unexpected does
        // throw, busy must still be released or the card becomes a dead end
        // with no retry and no dismiss.
        console.error('[Biometrics] startNativeLogin failed unexpectedly:', err);
        setBusy(false);
      });
  };

  return (
    <>
      {retryStatus && <ExchangeRetryToast status={retryStatus} />}
      {state && (
        <BiometricSurfaceHost
          native
          dismissible={!busy}
          onDismiss={busy ? undefined : close}
          ariaLabel={
            state === 'turned-off'
              ? 'Biometric sign-in turned off'
              : state === 'connection-failed'
              ? 'Biometric sign-in connection trouble'
              : 'Biometric sign-in'
          }>
          {state === 'not-completed' ? (
            <UnlockRetryCard
              skin='hijack'
              biometry={biometry}
              busy={busy}
              attempt={scanAttempt}
              maxAttempts={MAX_SCAN_ATTEMPTS}
              onRetry={retry}
              onUsePassword={usePassword}
              onClose={close}
            />
          ) : state === 'locked-out' ? (
            <LockedOutCard
              skin='hijack'
              biometry={biometry}
              busy={busy}
              attempt={scanAttempt}
              maxAttempts={MAX_SCAN_ATTEMPTS}
              onRetry={retry}
              onUsePassword={usePassword}
              onClose={close}
            />
          ) : state === 'connection-failed' ? (
            <ConnectionTroubleCard
              skin='hijack'
              biometry={biometry}
              busy={busy}
              onRetry={retry}
              onUsePassword={usePassword}
              onClose={close}
            />
          ) : (
            <BiometricsChangedCard
              skin='hijack'
              biometry={biometry}
              onUsePassword={usePassword}
              onDismiss={close}
            />
          )}
        </BiometricSurfaceHost>
      )}
    </>
  );
}

/**
 * Full-screen overlay, deliberately outside BiometricSurfaceHost: that host
 * exists to present a card (sheet on native, modal on desktop) tied to
 * `state`, but this can be showing WITH no state set at all — the first
 * attempt on a fresh login has no prior failure to have raised a card.
 *
 * Styled to match the app's own card language (disc, Inter heading, Oswald
 * micro-label) rather than a passing system-style toast — a network retry
 * during login is a "please wait, still working on it" moment, not a
 * background notification, so it gets the same visual weight as the cards
 * around it.
 */
function ExchangeRetryToast({ status }: { status: NonNullable<ExchangeRetryStatus> }) {
  return (
    <div
      role='status'
      aria-live='polite'
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 22,
        background: 'rgba(3,7,14,.6)',
        backdropFilter: 'blur(14px) saturate(150%)',
        WebkitBackdropFilter: 'blur(14px) saturate(150%)'
      }}>
      <span
        aria-hidden='true'
        style={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          background: 'rgba(232,130,31,.13)',
          border: '1px solid rgba(232,130,31,.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <span
          className='hj-biometric-retry-spin'
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            border: '3px solid rgba(255,180,92,.22)',
            borderTopColor: '#ffb45c',
            animation: 'hjBiometricRetrySpin 800ms linear infinite'
          }}
        />
      </span>
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontFamily: "'Inter',sans-serif",
            fontWeight: 700,
            fontSize: 19,
            letterSpacing: '.01em',
            color: '#f1e3c7'
          }}>
          Reconnecting…
        </div>
        <div
          style={{
            fontFamily: "'Oswald',sans-serif",
            fontWeight: 500,
            fontSize: 11,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'rgba(217,199,158,.72)',
            marginTop: 8
          }}>
          Attempt {status.attempt} of {status.maxAttempts}
        </div>
      </div>
      <style>{`
        @keyframes hjBiometricRetrySpin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          .hj-biometric-retry-spin { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
